import { effect } from '@preact/signals';
import {
  type ItemId,
  isEncodedTerrain,
  type ActionId, type MatchEvent, type MatchStart, type MatchSync, type PlayerInput, type PlayerOutcome, type RecruitKind,
  type ServerMessage, type WeaponId,
} from '@cratered/shared';
import { sfx } from './audio';
import { BotController } from './bots';
import { levelById } from './campaign';
import { InputController } from './input';
import { Renderer, type AimPreview } from './render';
import { mulberry32 } from './rng';
import {
  RemoteView, STAMINA, Sim, TICK_MS, UNIT_H, catapultNear, type Phase, type SimOptions, type Snapshot, type Unit, type ViewState, type VisualEvent,
} from './sim';
import { ITEMS, ITEM_IDS, MAX_TEAM_SIZE, PRICES, RECRUIT_PRICES, isPurchasable, type PurchasableId } from './economy';
import { MatchSounds } from './sounds';
import { ACTIONS, MENU_ORDER } from './weapons';
import type { Net } from '../net';
import { hostGrant, joinedInProgress, lobby } from '../store';

export interface HudTeam {
  team: number;
  name: string;
  hp: number;
  maxHp: number;
  alive: number;
  isEnemyAi: boolean;
}

export interface HudState {
  phase: Phase;
  myTurn: boolean;
  activePlayerId: string | null;
  activeName: string;
  secondsLeft: number;
  round: number;
  wave: number;
  wind: number;
  teams: HudTeam[];
  /** Every action, with ammo (Infinity = unlimited) and whether it can be picked right now. */
  menu: { id: ActionId; ammo: number; enabled: boolean; reason?: string }[];
  action: ActionId | null;
  /** Brawl: the round is live (everyone acts at once), what the local mole holds, and how much of its cooldown is left (0..1, 0 when ready). */
  live: boolean;
  held: ActionId | null;
  cooldown: number;
  stamina: number;
  moved: boolean;
  /** The move phase is waiting on gear: a hook in flight, or the mole on its line. */
  pulling: boolean;
  /** CSS px of the active mole's head, relative to the match area; null when off-screen. */
  anchor: { x: number; y: number } | null;
  ownUnits: { id: string; name: string; hp: number; maxHp: number; active: boolean }[];
  hint: string;
  /** The local player's team purse (null for spectators). */
  coins: number | null;
  /** Buy phase: what is for sale, what the team owns, and whether it can afford more. Attacks first, then gear. */
  shop: { weapon: PurchasableId; price: number; owned: number; canBuy: boolean; canSell: boolean; gear: boolean }[];
  recruits: { kind: RecruitKind; price: number; enabled: boolean; reason: string | null }[];
  /** Buy phase: one-off kit (the last-shot ghost), owned for the whole match once bought. */
  kit: { id: ItemId; name: string; blurb: string; price: number; owned: boolean; canBuy: boolean }[];
  crew: { size: number; max: number };
  /** Buy phase: whether this player pressed Ready, and every live team's readiness. */
  ready: boolean;
  readyTeams: { name: string; ready: boolean; isEnemyAi: boolean }[];
  /** Best-of-N scoreboard. */
  fight: { number: number; bestOf: number; wins: { team: number; name: string; wins: number; isEnemyAi: boolean }[] };
}

export interface MatchCallbacks {
  onHud(state: HudState): void;
  onCallout(text: string): void;
  onEnd(events: MatchEvent[], outcomes: PlayerOutcome[]): void;
}

const SNAPSHOT_EVERY = 4; // ticks → 15 Hz
/** A replica that still owes itself a full sync asks again this often (the first ask waits this long too). */
const SYNC_RETRY_MS = 1500;
/** With no snapshot for this long the host is gone (or on its way out); say so. */
const HOST_SILENT_MS = 3000;

function simOptionsFor(start: MatchStart): { opts: SimOptions; difficulty: number } {
  const level = levelById(start.settings.campaignLevelId);
  const enemies = start.settings.mode === 'campaign' && level
    ? level.enemies
    : start.settings.mode === 'coop'
      ? { count: 1 + start.players.length, hp: 70, difficulty: 0.45, name: 'Raiders' }
      : undefined;
  return {
    opts: { seed: start.seed, settings: start.settings, players: start.players, enemies, campaignLevelId: level?.id },
    difficulty: level?.enemies.difficulty ?? 0.5,
  };
}

/**
 * Owns one match on this client. The host runs the Sim and bots and broadcasts snapshots;
 * everyone else keeps a RemoteView in sync. Both render and take local input the same way.
 * Hosting can change hands mid-match: a replica is promoted from its replicated state when the
 * server says so, and a host that finds someone else's snapshots arriving steps down.
 */
export class MatchSession {
  private sim: Sim | null = null;
  private remote: RemoteView | null = null;
  private view: ViewState;
  /** The field as the renderer sees it; dev tooling only. */
  get debugView(): ViewState {
    return this.view;
  }
  private renderer: Renderer;
  private sounds: MatchSounds;
  private input: InputController;
  private bots: BotController | null = null;
  private raf = 0;
  private acc = 0;
  private last = performance.now();
  private unsubscribe: (() => void) | null = null;
  private aim: AimPreview | null = null;
  private pointer: { x: number; y: number } | null = null;
  private lastHudAt = 0;
  private lastTurnUnit: string | null = null;
  /** Brawl: the phase seen last frame, so the controls reset when the live phase starts and ends. */
  private lastPhase: Phase | null = null;
  /** Brawl: the local mole's current cooldown at its longest, so the HUD can show the fraction left. */
  private coolPeak = 0;
  private endedSent = false;
  private disposed = false;
  private pendingVisual: VisualEvent[] = [];
  /** Players the lobby reports disconnected; the bot plays for them whenever we host. */
  private away = new Set<string>();
  private stopEffects: (() => void)[] = [];
  private lastSnapshotAt = performance.now();
  private lastSyncRequestAt = performance.now();
  private lastHostSilentCalloutAt = 0;
  pip = false;

  get isHost(): boolean {
    return this.sim !== null;
  }

  constructor(
    readonly start: MatchStart,
    readonly localId: string,
    isHost: boolean,
    private readonly net: Net | null,
    private readonly canvas: HTMLCanvasElement,
    private readonly cb: MatchCallbacks,
  ) {
    if (isHost) {
      const { opts, difficulty } = simOptionsFor(start);
      this.sim = new Sim(opts);
      this.view = this.sim;
      this.bots = new BotController(difficulty, mulberry32(start.seed ^ 0x51ed));
    } else {
      this.remote = new RemoteView(start.settings, start.seed, start.players);
      // Joined (or reloaded) mid-match: the host owes us the terrain and state as they are now.
      this.remote.needsSync = joinedInProgress.value;
      this.view = this.remote;
    }
    this.renderer = new Renderer(canvas, this.view.level);
    this.renderer.setPlayers(start.players);
    this.sounds = new MatchSounds(sfx, localId);
    this.sounds.start(this.view.level);
    this.input = new InputController(canvas, {
      phase: () => this.view.turn.phase,
      live: () => this.live,
      canAct: () => this.canAct(),
      action: () => (this.brawl ? this.localUnit()?.action ?? null : this.view.turn.action),
      activeUnit: () => {
        const u = this.brawl ? this.localUnit() : this.view.units.find((x) => x.id === this.view.turn.unitId && x.alive);
        return u ? { id: u.id, x: u.x, y: u.y, facing: u.facing, aim: u.aim } : null;
      },
      ownUnits: () => this.view.units.filter((u) => u.alive && u.playerId === this.localId).map((u) => ({ id: u.id, x: u.x, y: u.y })),
      send: (input) => this.sendInput(input),
      screenToWorld: (px, py) => this.renderer.screenToWorld(px, py),
      preview: (aim) => (this.aim = aim),
      pointer: (p) => (this.pointer = p),
      menuPick: (i) => {
        const item = this.menuItems()[i];
        if (item?.enabled) this.chooseAction(item.id);
      },
    });
    if (net) this.unsubscribe = net.on((msg) => this.onNet(msg));
    this.stopEffects = [
      // Who is away, so the bot takes their turns while we host (now, or after a hand-over).
      effect(() => {
        const l = lobby.value;
        if (!l) return;
        for (const p of start.players) {
          const lp = l.players.find((x) => x.id === p.id);
          this.setAway(p.id, !lp || !lp.connected);
        }
      }),
      // The server handed us the simulation. Runs at once too, in case that arrived before we mounted.
      effect(() => {
        const g = hostGrant.value;
        if (g && g.seed === start.seed) this.promote();
      }),
    ];
    this.loop(performance.now());
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.sounds.stop();
    this.input.dispose();
    this.unsubscribe?.();
    for (const stop of this.stopEffects) stop();
  }

  /** Mark a player away (disconnected) so the host's bot takes their turns. */
  setAway(playerId: string, away: boolean): void {
    if (away) this.away.add(playerId);
    else this.away.delete(playerId);
    this.sim?.setAutoplay(playerId, away);
  }

  /** Run the simulation from here on, continuing from the replicated state when there is one. */
  promote(): void {
    if (this.sim) return;
    const { opts, difficulty } = simOptionsFor(this.start);
    const remote = this.remote;
    const sim = remote?.latest
      ? Sim.fromSnapshot(opts, remote.latest, remote.events, remote.terrain)
      : new Sim(opts);
    this.sim = sim;
    this.remote = null;
    this.view = sim;
    this.bots = new BotController(difficulty, mulberry32((this.start.seed ^ 0x51ed ^ sim.tick) >>> 0));
    for (const id of this.away) sim.setAutoplay(id, true);
    this.acc = 0;
    console.info(`[match] hosting from tick ${sim.tick}${remote?.latest ? ' (taken over from the previous host)' : ''}`);
    if (remote?.latest) this.cb.onCallout('You are hosting now');
  }

  /** Someone else runs the simulation now (we were away long enough to be replaced): replicate theirs. */
  private demote(): void {
    if (!this.sim) return;
    const remote = new RemoteView(this.start.settings, this.start.seed, this.start.players, this.sim.terrain);
    remote.needsSync = true;
    this.sim = null;
    this.bots = null;
    this.remote = remote;
    this.view = remote;
    console.info('[match] another client hosts now; following their snapshots');
    this.cb.onCallout('Another player is hosting now');
  }

  private applySync(sync: MatchSync): void {
    if (!this.remote || sync.seed !== this.start.seed) return;
    if (!isEncodedTerrain(sync.terrain) || !Array.isArray(sync.events) || !sync.snapshot || typeof sync.snapshot !== 'object') {
      console.warn('[match] ignoring a malformed terrain sync');
      return;
    }
    try {
      this.remote.applySync({ terrain: sync.terrain, events: sync.events, snapshot: sync.snapshot as Snapshot });
      this.lastSnapshotAt = performance.now();
      console.info(`[match] synced from the host at tick ${this.remote.tick} (${sync.events.length} events, ${sync.terrain.runs.length} chars of terrain)`);
    } catch (err) {
      console.warn('[match] bad terrain sync', err);
    }
  }

  chooseAction(action: ActionId): void {
    this.sendInput({ kind: 'action', action });
  }

  /** Brawl: put the next thing with ammo in hand (a controller's Triangle). */
  cycleAction(): void {
    if (!this.live) return;
    const items = this.menuItems().filter((m) => m.enabled && ACTIONS[m.id].kind !== 'melee' && ACTIONS[m.id].kind !== 'use');
    if (items.length === 0) return;
    const held = this.localUnit()?.action ?? null;
    const i = items.findIndex((m) => m.id === held);
    const next = items[(i + 1) % items.length];
    if (next && next.id !== held) this.chooseAction(next.id);
  }

  /** Brawl: boot whoever is in reach. */
  kick(): void {
    if (this.live) this.sendInput({ kind: 'kick' });
  }

  selectUnit(unitId: string): void {
    this.sendInput({ kind: 'select', unitId });
  }

  stopMove(): void {
    this.sendInput({ kind: 'stopMove' });
  }

  cancel(): void {
    this.sendInput({ kind: 'cancel' });
    this.aim = null;
  }

  move(dir: -1 | 0 | 1): void {
    this.input.move(dir);
  }

  jump(): void {
    this.input.jump();
  }

  skipTurn(): void {
    this.sendInput({ kind: 'skip' });
  }

  buy(weapon: PurchasableId, count: number): void {
    this.sendInput({ kind: 'buy', weapon, count });
  }

  recruit(kind: RecruitKind): void {
    this.sendInput({ kind: 'recruit', unit: kind });
  }

  buyItem(item: ItemId): void {
    this.sendInput({ kind: 'buyItem', item });
  }

  setReady(): void {
    this.sendInput({ kind: 'ready' });
  }

  // ---------- controller ----------

  get phase(): Phase {
    return this.view.turn.phase;
  }

  get myTurn(): boolean {
    return this.canAct();
  }

  padAim(angle: number, power: number, facing: 1 | -1): void {
    this.input.setAim(angle, power, facing);
  }

  padFire(): void {
    this.input.fire();
  }

  /** Cycle to the next of the local player's living moles (while picking). */
  nextUnit(): void {
    const own = this.view.units.filter((u) => u.alive && u.playerId === this.localId);
    const i = own.findIndex((u) => u.id === this.view.turn.unitId);
    const next = own[(i + 1) % own.length];
    if (next) this.selectUnit(next.id);
  }

  forfeit(): void {
    if (this.sim) this.sim.forfeit(this.localId);
    else this.net?.send({ t: 'forfeit' });
  }

  private get brawl(): boolean {
    return this.start.settings.mode === 'brawl';
  }

  /** Brawl: the round is live, so every player steers their own mole at once. */
  private get live(): boolean {
    return this.brawl && this.view.turn.phase === 'live';
  }

  /** Brawl: the local player's one living mole (null for spectators and once it is gone). */
  private localUnit(): Unit | null {
    if (this.sim) return this.sim.liveUnit(this.localId);
    return this.view.units.find((u) => u.alive && u.playerId === this.localId) ?? null;
  }

  private canAct(): boolean {
    const t = this.view.turn;
    if (this.view.ended) return false;
    if (this.brawl) return t.phase === 'live' && this.localUnit() !== null;
    if (t.playerId !== this.localId) return false;
    return t.phase === 'select' || t.phase === 'move' || t.phase === 'aim';
  }

  private sendInput(input: PlayerInput): void {
    if (this.sim) this.sim.applyInput(this.localId, input);
    else this.net?.send({ t: 'input', input });
  }

  private onNet(msg: ServerMessage): void {
    if (msg.t === 'input' && this.sim) this.sim.applyInput(msg.from, msg.input);
    else if (msg.t === 'forfeit' && this.sim) this.sim.forfeit(msg.from);
    else if (msg.t === 'sim') {
      // Snapshots only come from the current host; if we still think that is us, we were replaced.
      if (this.sim) this.demote();
      this.remote!.apply(msg.payload as Snapshot);
      this.lastSnapshotAt = performance.now();
    } else if (msg.t === 'terrainSync') {
      if (this.sim) this.demote();
      this.applySync(msg.sync);
    } else if (msg.t === 'syncRequest' && this.sim) {
      this.net?.send({ t: 'terrainSync', to: msg.for, sync: { seed: this.start.seed, ...this.sim.sync() } });
    }
  }

  private loop = (now: number): void => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.min(250, now - this.last);
    this.last = now;

    if (this.sim) {
      this.acc += dt;
      let ticks = 0;
      while (this.acc >= TICK_MS && ticks < 8) {
        this.sim.step();
        this.bots?.update(this.sim);
        this.acc -= TICK_MS;
        ticks++;
        if (this.net && this.sim.tick % SNAPSHOT_EVERY === 0) {
          const snap = this.sim.snapshot();
          this.net.send({ t: 'sim', payload: snap });
          this.pendingVisual.push(...snap.visual);
        }
      }
      if (this.sim.ended && !this.endedSent && this.sim.outcomes) {
        this.endedSent = true;
        const events = this.sim.events;
        const outcomes = this.sim.outcomes;
        if (this.net) this.net.send({ t: 'matchEnd', events, outcomes });
        setTimeout(() => this.cb.onEnd(events, outcomes), 2200);
      }
    } else if (this.remote) {
      this.remote.update();
      if (this.net && this.remote.needsSync && now - this.lastSyncRequestAt > SYNC_RETRY_MS) {
        this.lastSyncRequestAt = now;
        this.net.send({ t: 'requestSync' });
      }
      if (!this.remote.ended && now - this.lastSnapshotAt > HOST_SILENT_MS && now - this.lastHostSilentCalloutAt > 4000) {
        this.lastHostSilentCalloutAt = now;
        this.cb.onCallout('Waiting for the host…');
      }
    }

    const visual = this.view.drainVisual().concat(this.pendingVisual.splice(0));
    this.sounds.frame(this.view, visual);
    const forRenderer: VisualEvent[] = [];
    for (const ev of visual) {
      if (ev.kind === 'callout') this.cb.onCallout(ev.text);
      else if (ev.kind === 'wave') this.cb.onCallout(`Wave ${ev.wave}!`);
      else if (ev.kind === 'turn') {
        // No turns in a brawl: everyone is up at once, so the callout would only mislead.
        if (!this.brawl && ev.playerId === this.localId && ev.unitId !== this.lastTurnUnit) this.cb.onCallout('Your turn');
      } else forRenderer.push(ev);
    }
    this.renderView.setVisual(forRenderer);

    if (this.view.turn.unitId !== this.lastTurnUnit || this.view.turn.phase === 'between') {
      if (this.view.turn.unitId !== this.lastTurnUnit) this.input.resetTurn();
      this.lastTurnUnit = this.view.turn.unitId;
      this.aim = null;
    }
    const phase = this.view.turn.phase;
    if (this.brawl && phase !== this.lastPhase) {
      // Into or out of the live phase: drop any half-set shot, and stop a mole a held key left walking.
      if (phase === 'live' || this.lastPhase === 'live') this.input.resetTurn();
      this.lastPhase = phase;
    }
    if (phase !== 'aim' && !this.live) this.aim = null;

    this.renderer.frame({
      view: this.renderView,
      players: this.start.players,
      localPlayerId: this.localId,
      focusUnitId: this.brawl ? this.localUnit()?.id ?? null : null,
      aim: this.canAct() ? this.aim : null,
      pointer: this.canAct() ? this.pointer : null,
      selecting: this.canAct() && this.view.turn.phase === 'select' && !this.view.turn.moved,
      pip: this.pip,
    });

    // The live phase refreshes faster so the cooldown sweep on the held tile runs smoothly.
    const hudEvery = this.view.turn.phase === 'select' ? 33 : this.view.turn.phase === 'buy' ? 150 : this.live ? 50 : 100;
    if (now - this.lastHudAt > hudEvery) {
      this.lastHudAt = now;
      this.cb.onHud(this.hudState());
    }
  };

  /** The renderer drains visuals itself; this proxy hands it only the ones we already filtered. */
  private renderView: ViewState & { setVisual(v: VisualEvent[]): void } = (() => {
    let queued: VisualEvent[] = [];
    const self = this;
    return {
      get level() { return self.view.level; },
      get terrain() { return self.view.terrain; },
      get boats() { return self.view.boats; },
      get mines() { return self.view.mines; },
      get catapults() { return self.view.catapults; },
      get units() { return self.view.units; },
      get projectiles() { return self.view.projectiles; },
      get turn() { return self.view.turn; },
      get wind() { return self.view.wind; },
      get teams() { return self.view.teams; },
      get ended() { return self.view.ended; },
      get tick() { return self.view.tick; },
      drainVisual() { const v = queued; queued = []; return v; },
      trailFor(id: string) { return self.view.trailFor(id); },
      setVisual(v) { queued = queued.concat(v); },
    };
  })();

  private menuItems(): HudState['menu'] {
    const v = this.view;
    const unit = this.brawl ? this.localUnit() : v.units.find((u) => u.id === v.turn.unitId && u.alive);
    const team = v.teams.find((t) => t.playerIds.includes(this.localId));
    if (!unit || !team) return [];
    if (this.brawl) return this.liveMenuItems(unit, team);
    const inReach = (reach: number): boolean => this.sim
      ? !!this.sim.meleeTarget(unit, reach)
      : v.units.some((u) => u.alive && u.team !== unit.team && Math.abs(u.x - unit.x) <= reach && Math.abs(u.y - unit.y) <= 50);
    const deck = unit.boat !== null ? v.boats[unit.boat] : null;
    const canRepair = !!deck && !deck.sunk && deck.panels.some((p) => !p.alive);
    const items: HudState['menu'] = [];
    const hasCatapults = v.catapults.length > 0;
    for (const id of MENU_ORDER) {
      // The catapult is a map emplacement: no tile for it on maps without one.
      if (ACTIONS[id].station && !hasCatapults) continue;
      let ammo = id === 'move' || id === 'skip' ? Infinity : (team.ammo[id as WeaponId] ?? 0);
      let enabled = ammo > 0;
      let reason: string | undefined;
      if (!enabled && id !== 'move' && id !== 'skip') reason = 'Buy some in the next buy phase';
      if (ACTIONS[id].station) {
        const station = catapultNear(v.catapults, unit);
        ammo = station ? station.boulders : 0;
        if (!station) {
          enabled = false;
          reason = v.catapults.some((c) => c.boulders > 0) ? 'Stand beside a catapult with launches left' : 'Every catapult is spent this round';
        }
      }
      if (id === 'move' && v.turn.moved) {
        enabled = false;
        reason = 'Already moved';
      }
      if (id === 'move' && unit.floatie) {
        enabled = false;
        reason = 'Floaties stay put';
      }
      if (ACTIONS[id].kind === 'melee' && enabled && !inReach(ACTIONS[id].reach ?? 64)) {
        enabled = false;
        reason = 'No one in reach';
      }
      if (ACTIONS[id].gear && v.turn.moved) {
        enabled = false;
        reason = 'Gear takes your move, and you have moved';
      }
      if (id === 'repair' && !canRepair) {
        enabled = false;
        reason = deck ? 'This raft is whole' : 'Stand on a damaged raft';
      }
      if (id === 'medkit' && unit.hp >= unit.maxHp) {
        enabled = false;
        reason = 'Full health';
      }
      items.push({ id, ammo, enabled, reason });
    }
    return items;
  }

  /**
   * Brawl: no move or skip tiles (walking is free and there is no turn to skip), and a tile is
   * usable whenever there is ammo for it. The cooldown is shown on the held tile, not as a
   * disabled one, so the next weapon can be picked while the last one recovers.
   */
  private liveMenuItems(unit: Unit, team: ViewState['teams'][number]): HudState['menu'] {
    const v = this.view;
    const items: HudState['menu'] = [];
    const hasCatapults = v.catapults.length > 0;
    for (const id of MENU_ORDER) {
      if (id === 'move' || id === 'skip') continue;
      if (ACTIONS[id].station && !hasCatapults) continue;
      if (ACTIONS[id].pveOnly) continue;
      let ammo = team.ammo[id as WeaponId] ?? 0;
      let enabled = ammo > 0;
      let reason: string | undefined;
      if (!enabled) reason = 'Buy some in the next buy phase';
      if (ACTIONS[id].station) {
        const station = catapultNear(v.catapults, unit);
        ammo = station ? station.boulders : 0;
        if (!station) {
          enabled = false;
          reason = v.catapults.some((c) => c.boulders > 0) ? 'Stand beside a catapult with launches left' : 'Every catapult is spent this round';
        }
      }
      items.push({ id, ammo, enabled, reason });
    }
    return items;
  }

  private hudState(): HudState {
    const v = this.view;
    const teams: HudTeam[] = v.teams.map((t) => {
      const units = v.units.filter((u) => u.team === t.team);
      return {
        team: t.team,
        name: t.name,
        hp: units.reduce((s, u) => s + Math.max(0, u.hp), 0),
        maxHp: units.reduce((s, u) => s + u.maxHp, 0),
        alive: units.filter((u) => u.alive).length,
        isEnemyAi: t.isEnemyAi,
      };
    });
    const isLive = this.live;
    const mine = this.brawl ? this.localUnit() : null;
    const active = mine ?? v.units.find((u) => u.id === v.turn.unitId);
    const myTurn = this.canAct();
    const phase = v.turn.phase;
    let anchor: HudState['anchor'] = null;
    if (active && myTurn && (phase === 'select' || isLive)) {
      const s = this.renderer.worldToScreen(active.x, active.y - UNIT_H - 44);
      anchor = { x: s.x, y: s.y };
    }
    const held = mine?.action ?? null;
    const action = this.brawl ? held : v.turn.action;
    // The sim only keeps the ticks left, so the length of the current cooldown is its peak since it
    // last hit zero (the thing that set it may since have run out and fallen back to the ball).
    this.coolPeak = mine && mine.cooldown > 0 ? Math.max(this.coolPeak, mine.cooldown) : 0;
    const cooldown = isLive && mine && mine.cooldown > 0 ? Math.round(Math.min(1, mine.cooldown / this.coolPeak) * 100) / 100 : 0;
    const pulling = phase === 'move' && !!active && (active.pull !== null || v.projectiles.some((p) => p.alive));
    let hint = '';
    if (myTurn && isLive) hint = 'Arrows or A/D walk, W jumps, drag or click to fire, 1-9 pick a weapon, E boots.';
    else if (myTurn) {
      if (phase === 'select') hint = v.turn.moved ? 'Pick an attack, or skip.' : 'Pick a mole and an action. Moving or using gear does not end your turn.';
      else if (phase === 'move') hint = pulling ? 'Hang on…' : ACTIONS.move.hint;
      else if (phase === 'aim' && action) {
        const def = ACTIONS[action];
        hint = def.kind === 'throw'
          ? def.dig ? 'Drag from your mole to aim the tunnel, release to dig. Esc goes back.'
            : def.place === 'self' ? 'Drag from your mole to where you want to be, release to blink. Esc goes back.'
              : def.place === 'charge' ? 'Drag from your mole to where the charge goes (within reach), release to set it. Esc goes back.'
                : def.place ? 'Drag from your mole to where the plank goes, release to set it down. Esc goes back.'
                  : def.strike ? 'Drag sideways to mark the spot (a longer drag reaches farther), release to call it in. Esc goes back.'
                    : def.station ? 'Drag from the catapult to aim your flight, release to climb in and go. Esc goes back.'
                      : def.fixedPower ? 'Drag from your mole to aim the hook, release to fire. Esc goes back.'
                        : 'Drag from your mole to aim, release to throw. Esc goes back.'
          : 'Point the crosshair, click to shoot. Esc goes back.';
      }
    } else if (phase === 'buy') hint = 'Buy phase: spend your coins, then press Ready.';
    else if (phase === 'ended') hint = 'Match over.';
    else if (isLive) hint = this.start.players.some((p) => p.id === this.localId) ? 'Your mole is out. Watch the rest of the round.' : 'Brawl: last team standing wins.';
    else if (active && !this.brawl) hint = `${active.name} is up.`;

    const myTeam = v.teams.find((t) => t.playerIds.includes(this.localId)) ?? null;
    const live = v.teams.filter((t) => v.units.some((u) => u.alive && u.team === t.team));
    const crewSize = myTeam ? v.units.filter((u) => u.alive && u.team === myTeam.team).length : 0;
    const shop = myTeam
      ? MENU_ORDER.filter((id): id is PurchasableId => id !== 'move' && id !== 'skip' && isPurchasable(id)).map((weapon) => ({
          weapon: weapon as PurchasableId,
          price: PRICES[weapon],
          owned: myTeam.ammo[weapon] ?? 0,
          canBuy: myTeam.coins >= PRICES[weapon],
          canSell: (myTeam.ammo[weapon] ?? 0) > 0,
          gear: !!ACTIONS[weapon].gear,
        }))
      : [];
    const recruits = myTeam
      ? (['floatie', 'boat'] as RecruitKind[]).map((kind) => {
          const reason = this.sim ? this.sim.recruitBlocker(myTeam, kind)
            : crewSize >= MAX_TEAM_SIZE ? 'Crew is full' : myTeam.coins < RECRUIT_PRICES[kind] ? 'Not enough coins' : null;
          return { kind, price: RECRUIT_PRICES[kind], enabled: !reason, reason };
        })
      : [];
    const kit = myTeam
      ? ITEM_IDS.map((id) => ({
          id,
          name: ITEMS[id].name,
          blurb: ITEMS[id].blurb,
          price: ITEMS[id].price,
          owned: myTeam.items?.[id] ?? false,
          canBuy: !myTeam.items?.[id] && myTeam.coins >= ITEMS[id].price,
        }))
      : [];
    return {
      phase,
      myTurn,
      activePlayerId: v.turn.playerId,
      activeName: active ? active.name : '',
      secondsLeft: Math.max(0, Math.ceil(v.turn.ticksLeft / 60)),
      round: v.turn.round,
      wave: v.turn.wave,
      wind: v.wind,
      teams,
      menu: myTurn && (phase === 'select' || isLive) ? this.menuItems() : [],
      action,
      live: isLive,
      held,
      cooldown,
      stamina: Math.max(0, v.turn.stamina / (STAMINA * v.level.stamina)),
      moved: v.turn.moved,
      pulling,
      anchor,
      ownUnits: v.units.filter((u) => u.playerId === this.localId && u.alive).map((u) => ({ id: u.id, name: u.name, hp: u.hp, maxHp: u.maxHp, active: u.id === (mine?.id ?? v.turn.unitId) })),
      hint,
      coins: myTeam ? myTeam.coins : null,
      shop,
      recruits,
      kit,
      crew: { size: crewSize, max: MAX_TEAM_SIZE },
      ready: this.sim ? this.sim.isPlayerReady(this.localId) : (myTeam?.ready ?? false),
      readyTeams: live.map((t) => ({ name: t.name, ready: t.ready, isEnemyAi: t.isEnemyAi })),
      fight: {
        number: v.turn.fight,
        bestOf: v.turn.bestOf,
        wins: v.teams.map((t) => ({ team: t.team, name: t.name, wins: v.turn.wins[t.team] ?? 0, isEnemyAi: t.isEnemyAi })),
      },
    };
  }
}
